interface BookWithAuthorData {
  rating_star?: number;
  books?: {
    id: string;
    title: string;
    authors: string[];
    page_count?: number;
    published_year?: number;
    average_rating?: number;
    rating_count?: number;
  };
}

export interface AuthorDiversityStats {
  uniqueAuthors: number;
  totalBooks: number;
  diversityScore: number; // 0-100, higher = more diverse
  topAuthors: Array<{ author: string; count: number; percentage: number }>;
  readingPattern: 'diverse' | 'focused' | 'balanced';
}

export interface PageCountPreference {
  bookId: string;
  title: string;
  pageCount: number;
  rating: number;
  authors: string[];
}

export interface PublicationYearTrend {
  decade: string;
  count: number;
  averageRating: number;
}

export interface ComparativeRating {
  bookId: string;
  title: string;
  authors: string[];
  userRating: number;
  communityRating: number;
  difference: number;
  agreementType: 'loved_more' | 'loved_less' | 'aligned';
}

function normalizeAuthorName(author: string): string {
  return author
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .replace(/\.\s+/g, '.')
    .replace(/\s*\.\s*/g, '.')
    .trim();
}

export function calculateAuthorDiversity(books: BookWithAuthorData[]): AuthorDiversityStats {
  const authorMap = new Map<string, { count: number; displayName: string }>();
  let totalBooks = 0;

  books.forEach(book => {
    if (!book.books?.authors) return;
    
    totalBooks++;
    book.books.authors.forEach(author => {
      const normalized = normalizeAuthorName(author);
      const current = authorMap.get(normalized) || { count: 0, displayName: author };
      current.count++;
      authorMap.set(normalized, current);
    });
  });

  const uniqueAuthors = authorMap.size;
  
  // Calculate diversity score (0-100)
  // Perfect diversity = each author read once = 100
  // All books from one author = 0
  const diversityScore = totalBooks > 0
    ? Math.round((uniqueAuthors / totalBooks) * 100)
    : 0;

  const topAuthors = Array.from(authorMap.values())
    .map(({ displayName, count }) => ({
      author: displayName,
      count,
      percentage: Math.round((count / totalBooks) * 100),
    }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 10);

  // Determine reading pattern
  const topAuthorPercentage = topAuthors[0]?.percentage || 0;
  const readingPattern = 
    diversityScore >= 75 ? 'diverse' :
    topAuthorPercentage >= 30 ? 'focused' :
    'balanced';

  return {
    uniqueAuthors,
    totalBooks,
    diversityScore,
    topAuthors,
    readingPattern,
  };
}

export function getPageCountPreferences(books: BookWithAuthorData[]): PageCountPreference[] {
  return books
    .filter(book => 
      book.books?.page_count && 
      book.books.page_count > 0 &&
      book.rating_star
    )
    .map(book => ({
      bookId: book.books!.id,
      title: book.books!.title,
      pageCount: book.books!.page_count!,
      rating: book.rating_star!,
      authors: book.books!.authors || [],
    }))
    .sort((a, b) => a.pageCount - b.pageCount);
}

export function calculatePublicationYearTrends(books: BookWithAuthorData[]): PublicationYearTrend[] {
  const decadeMap = new Map<string, { count: number; totalRating: number; ratedCount: number }>();

  books.forEach(book => {
    if (!book.books?.published_year) return;

    const decade = Math.floor(book.books.published_year / 10) * 10;
    const decadeLabel = `${decade}s`;

    const current = decadeMap.get(decadeLabel) || { count: 0, totalRating: 0, ratedCount: 0 };
    current.count++;

    if (book.rating_star) {
      current.totalRating += book.rating_star;
      current.ratedCount++;
    }

    decadeMap.set(decadeLabel, current);
  });

  return Array.from(decadeMap.entries())
    .map(([decade, data]) => ({
      decade,
      count: data.count,
      averageRating: data.ratedCount > 0 ? data.totalRating / data.ratedCount : 0,
    }))
    .sort((a, b) => {
      const decadeA = parseInt(a.decade);
      const decadeB = parseInt(b.decade);
      return decadeA - decadeB;
    });
}

export function calculateComparativeStats(books: BookWithAuthorData[]): ComparativeRating[] {
  return books
    .filter(book => 
      book.rating_star &&
      book.books?.average_rating &&
      book.books?.rating_count &&
      book.books.rating_count >= 5 // Only compare books with enough community ratings
    )
    .map(book => {
      const difference = book.rating_star! - book.books!.average_rating!;
      
      let agreementType: 'loved_more' | 'loved_less' | 'aligned';
      if (difference > 0.5) {
        agreementType = 'loved_more';
      } else if (difference < -0.5) {
        agreementType = 'loved_less';
      } else {
        agreementType = 'aligned';
      }

      return {
        bookId: book.books!.id,
        title: book.books!.title,
        authors: book.books!.authors || [],
        userRating: book.rating_star!,
        communityRating: book.books!.average_rating!,
        difference,
        agreementType,
      };
    })
    .sort((a, b) => Math.abs(b.difference) - Math.abs(a.difference));
}
